import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import PayeeForm from '../components/PayeeForm';
import PageSuggestion from '../components/PageSuggestion';
import { payeeApi } from '../services/api';
import { Payee, CreatePayeeRequest, UpdatePayeeRequest } from '../types';

const Payees: React.FC = () => {
    const [payees, setPayees] = useState<Payee[]>([]);
    const [filteredPayees, setFilteredPayees] = useState<Payee[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPayee, setEditingPayee] = useState<Payee | null>(null);
    const [loading, setLoading] = useState(true);

    const itemsPerPage = 10;

    useEffect(() => {
        fetchPayees();
    }, []);

    useEffect(() => {
        const filtered = payees.filter(payee =>
            payee.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredPayees(filtered);
        setCurrentPage(1);
    }, [searchTerm, payees]);

    const fetchPayees = async () => {
        try {
            setLoading(true);
            const response = await payeeApi.getAll();
            setPayees(response.data || []);
        } catch (error) {
            console.error('Erro ao buscar favorecidos:', error);
            setPayees([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (data: CreatePayeeRequest) => {
        try {
            await payeeApi.create(data);
            await fetchPayees();
            setIsModalOpen(false);
        } catch (error) {
            console.error('Erro ao criar favorecido:', error);
        }
    };

    const handleUpdate = async (data: UpdatePayeeRequest) => {
        if (!editingPayee) return;

        try {
            await payeeApi.update(editingPayee.id, data);
            await fetchPayees();
            setIsModalOpen(false);
            setEditingPayee(null);
        } catch (error) {
            console.error('Erro ao atualizar favorecido:', error);
        }
    };

    const handleEdit = (payee: Payee) => {
        setEditingPayee(payee);
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        setEditingPayee(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingPayee(null);
    };

    const handleSubmit = async (data: CreatePayeeRequest | UpdatePayeeRequest) => {
        if (editingPayee) {
            await handleUpdate(data as UpdatePayeeRequest);
        } else {
            await handleCreate(data as CreatePayeeRequest);
        }
    };

    const columns = [
        { key: 'name', label: 'Nome' },
        { key: 'description', label: 'Descrição' },
        { key: 'category', label: 'Categoria' },
        {
            key: 'createdAt',
            label: 'Criado em',
            render: (value: string) => new Date(value).toLocaleDateString('pt-BR')
        }
    ];

    const totalPages = Math.ceil(filteredPayees.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedData = filteredPayees.slice(startIndex, startIndex + itemsPerPage);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-secondary-600">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
                <div>
                    <h1 className="text-2xl font-bold text-secondary-900">Favorecidos</h1>
                    <p className="text-secondary-600">Gerencie seus favorecidos e categorias</p>
                    <PageSuggestion>
                        Cadastre empresas e pessoas para vincular pagamentos recorrentes, contas e lancamentos com mais rapidez.
                    </PageSuggestion>
                </div>
                <button
                    onClick={handleAddNew}
                    className="btn-primary flex items-center justify-center"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Favorecido
                </button>
            </div>

            <DataTable
                data={paginatedData}
                columns={columns}
                searchPlaceholder="Buscar por nome..."
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onEdit={handleEdit}
                editButtonText="Editar"
            />

            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={editingPayee ? 'Editar Favorecido' : 'Adicionar Favorecido'}
            >
                <PayeeForm
                    payee={editingPayee}
                    onSubmit={handleSubmit}
                    onCancel={handleCloseModal}
                />
            </Modal>
        </div>
    );
};

export default Payees;
